<script setup lang="ts">
import { ROUTE_NAMES } from '~/constants/routes';
import { isAuction, type PlatformSaleItemEntity } from '~/types/sale.type';

// PROPS
interface Props {
    sale: PlatformSaleItemEntity;
    embedded?: boolean;
}

const { sale, embedded = false } = defineProps<Props>();

// COMPOSABLES
const { tt } = useTypedI18n();
const { statusSectionColor, isSeller, inspectionReminder } = isAuction(sale)
    ? useAuctionStatus(toRef(() => sale))
    : useDirectSaleStatus(toRef(() => sale));
const { isSalesRep, isCustomerService } = useAuthUtils();
const canViewStats = computed(() => !!sale.canActForSellerCompany || isSalesRep.value || isCustomerService.value);
</script>

<template>
    <div class="divide-y divide-solid divide-neutral-200" :class="{ 'overflow-hidden rounded-xl border border-solid border-neutral-200': !embedded }">
        <div class="p-4 lg:p-6" :class="statusSectionColor">
            <SdpSalePanelStatusSection :sale="sale" />
        </div>

        <SdpInspectionReminderAlert v-if="inspectionReminder" :expiry-status="inspectionReminder" class="rounded-none" variant="soft" />

        <AdpSalePanelPlaceBidSection v-if="isAuction(sale)" :auction="sale" />
        <DsdpSalePanelActionsSection v-else :direct-sale="sale" />
        <SdpSalePanelVas v-if="!isSeller" :sale="sale" />
        <SdpSalePanelOrderSection v-if="sale.orderId" :order-id="sale.orderId" />

        <div v-if="canViewStats" class="p-4 lg:p-6">
            <NuxtLinkLocale
                :to="{
                    name: isAuction(sale) ? ROUTE_NAMES.ACCOUNT_AUCTIONS_VEHICLE_STATS : ROUTE_NAMES.ACCOUNT_DIRECT_SALES_VEHICLE_STATS,
                    params: { id: sale.id },
                }"
                class="text-primary flex items-center gap-2 text-sm font-medium"
            >
                <UIcon name="lucide:bar-chart-2" class="size-4" />
                {{ tt('sellerDashboard.viewStats') }}
            </NuxtLinkLocale>
        </div>
    </div>
</template>
